import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { HelmetDatoCms } from "gatsby-source-datocms";
import { Helmet } from "react-helmet";

import remoteAssetUrl from "../../lib/remote-asset-url";

const lang = process.env.GATSBY_APP_LANG;

function withSeo(WrappedComponent) {
    const hocComponent = ({
        seo: {
            url,
            metaRobots,
            metaCanonical,
            seoMetaTags = { tags: [] },
            localeVariants = [],
            children
        },
        pageContext,
        data,
        includedDomains,
        ...props
    }) => {
        const domains = data.domains.nodes;

        const pathSegment = pageContext?.pathSeg || "";

        // .filter(
        //     ({ hreflang }) => hreflang !== pageContext.locale
        // );

        const metaIndexing =
            !metaRobots ||
            /^index/.test(metaRobots || "") ||
            metaRobots === "all";

        const seoData = seoMetaTags || { tags: [] };

        seoData.tags = seoData.tags
            .filter((s) => {
                if (
                    !s.attributes ||
                    (!s.attributes.property && s.attributes.name !== "robots")
                ) {
                    return true;
                }

                if (s.attributes && s.attributes.name === "robots") {
                    return false;
                }

                return (
                    s.attributes.property !== "article:modified_time" &&
                    s.attributes.property !== "article:published_time"
                );
            })
            .map((tag) => {
                if (
                    !["og:image", "twitter:image"].includes(
                        tag?.attributes?.property || tag?.attributes?.name
                    )
                ) {
                    return tag;
                }

                return {
                    ...tag,
                    attributes: {
                        ...tag.attributes,
                        content: remoteAssetUrl(tag.attributes.content)
                    }
                };
            });

        const hasUS = data.alternatives._allSlugLocales.some(
            ({ locale }) => locale === "en-US"
        );
        const hasGB = data.alternatives._allSlugLocales.some(
            ({ locale }) => locale === "en-GB"
        );

        const alternatives = metaIndexing
            ? data.alternatives._allSlugLocales
            : [];

        // Had to add en-US and en-GB as alternate links in case en fallback was used to create page.
        const hreflangs = alternatives
            .filter(({ locale }) => {
                const variant = localeVariants.find(
                    (variant) => variant.locale === locale
                );
                return variant?.indexable ?? true;
            })
            .reduce((prev, { locale, value }) => {
                // There are often not as much sub-pages in chinese then in other languages which could lead to 404 references
                // Don't set hreflang TO chinese page with pathSegment or ON chinese page with pathSegment
                if (
                    !!pathSegment &&
                    ((pageContext?.locale !== "zh-CN" && locale === "zh-CN") ||
                        pageContext?.locale === "zh-CN")
                ) {
                    return prev;
                }

                const newValue = `${value}${pathSegment}`;

                if (locale !== "en") {
                    return [...prev, { locale, value: newValue }];
                }

                const returnArray = [...prev, { locale, value: newValue }];

                if (!hasUS) {
                    returnArray.push({
                        locale: "en-US",
                        value: newValue
                    });
                }

                if (!hasGB) {
                    returnArray.push({
                        locale: "en-GB",
                        value: newValue
                    });
                }

                return returnArray;
            }, [])
            // Cameras can be excluded from certain languages, this is why we need to filter them out
            .filter(
                ({ locale }) =>
                    !includedDomains ||
                    includedDomains.some(
                        ({ cmsLanguage }) => locale === cmsLanguage
                    )
            );

        // No hreflang Tags on microsites and on the camera listing pager pages
        const hideHrefLang =
            pageContext.isMicroSite ||
            (pageContext.layoutId === "camera_listing" && !!pathSegment);

        let canonicalSlug = (
            metaCanonical?.url ||
            pageContext?.slug ||
            ""
        ).replace("/index/", "");

        // On camera listing pager pages the canonical should be set to the first page
        if (pageContext.layoutId === "camera_listing") {
            canonicalSlug = canonicalSlug.replace(/\/p\/\d+\/$/, "/");
        }

        // (metaCanonical?.url === "/index/" ? "" : metaCanonical?.url) ||
        // (url === "/index/" ? "" : url);

        return (
            <>
                <HelmetDatoCms seo={seoData}>
                    <html lang={pageContext.locale} />

                    {metaRobots && (
                        <meta
                            name="robots"
                            content={metaRobots.replace(/ /, ",")}
                        />
                    )}

                    {metaIndexing && (
                        <link
                            rel="canonical"
                            href={`${process.env.GATSBY_BASE_URL.replace(
                                /\/$/,
                                ""
                            )}${canonicalSlug}`}
                        />
                    )}

                    {children}
                </HelmetDatoCms>

                {!hideHrefLang && (
                    <Helmet>
                        {hreflangs.map(({ locale, value: slug }, index) => {
                            const domain = (
                                domains.find(
                                    ({ hreflang }) => hreflang === locale
                                ) ?? {}
                            ).domain;

                            if (!domain) return null;

                            return (
                                <link
                                    rel="alternate"
                                    key={index}
                                    hreflang={locale}
                                    href={`${domain}${
                                        slug === "/index/" ? "/" : slug
                                    }`}
                                />
                            );
                        })}
                    </Helmet>
                )}

                <WrappedComponent
                    data={data}
                    pageContext={pageContext}
                    {...props}
                />
            </>
        );
    };

    hocComponent.propTypes = {
        seo: PropTypes.object.isRequired
    };

    return hocComponent;
}

export default withSeo;
